<template>
  <div>
    <default>

      <div class="flex items-center justify-between mb-4">
        <h1 class="text-2xl font-bold">Logy</h1>
        <button @click="fetchLogs" :disabled="isLoading"
                class="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-300">
          <svg v-if="isLoading" class="animate-spin h-5 w-5 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none"
               viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v2a6 6 0 100 12v2a8 8 0 01-8-8z"></path>
          </svg>
          <span v-if="!isLoading">Obnovit</span>
          <span v-else>Načítám...</span>
        </button>
      </div>
      <p class="mb-4">Zde můžete vidět všechny logy, které byly zaznamenány v aplikaci.</p>

      <!-- Loading Spinner -->
      <div v-if="isLoading && !logs.length " class="flex justify-center items-center my-4">
        <div class="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12"></div>
      </div>


      <div v-else :class="{ 'opacity-50': isLoading }" class="relative overflow-x-auto shadow-md sm:rounded-lg">
        <table class="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
          <thead class="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
          <tr>

            <th scope="col" class="px-6 py-3">
              Datum
            </th>
            <th scope="col" class="px-6 py-3">
              Úroveň
            </th>
            <th scope="col" class="px-6 py-3">
              Typ
            </th>
            <th scope="col" class="px-6 py-3">
              Zdroj
            </th>

            <th scope="col" class="px-6 py-3">
              Zpráva
            </th>
            <th scope="col" class="px-6 py-3">
              Log id
            </th>
          </tr>
          </thead>
          <tbody>

          <tr v-for="log in logs" :key="log.id"
              class="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">

            <th scope="row" class="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">
              {{ new Date(log.createdAt).toLocaleString() }}
            </th>
            <td class="px-6 py-4">
              {{ log.level }}
            </td>
            <td class="px-6 py-4">
              {{ log.type }}
            </td>
            <td class="px-6 py-4">
              {{ log.source }}
            </td>
            <td class="px-6 py-4 w-1/2">
              {{ log.message }}
            </td>
            <td class="px-6 py-4">
              <small>{{ log.id }}</small>
            </td>
          </tr>

          </tbody>
        </table>
        <nav class="flex items-center flex-column flex-wrap md:flex-row justify-between py-4 px-6"
             aria-label="Table navigation">
          <div>
            <label for="limit" class="mr-2 text-sm text-gray-700">Počet na stránku:</label>
            <select v-model="limit" @change="fetchLogs" id="limit"
                    class="text-sm text-gray-700 bg-white border border-gray-300 rounded-md h-8 px-3 py-0">
              <option :value="10">10</option>
              <option :value="20">20</option>
              <option :value="50">50</option>
            </select>
          </div>
          <div>
          <span
              class="text-sm font-normal text-gray-500 dark:text-gray-400 mb-4 md:mb-0 block w-full md:inline md:w-auto">Zobrazuji
            <span class="font-semibold text-gray-900 dark:text-white">{{
                (currentPage - 1) * limit + 1
              }} - {{ logs.length }}</span>
            z <span class="font-semibold text-gray-900 dark:text-white">{{ totalCount }}</span></span>
          </div>

          <ul class="inline-flex -space-x-px rtl:space-x-reverse text-sm h-8">
            <li>
              <a href="#" @click="previousPage" :disabled="currentPage === 1"
                 class="flex items-center justify-center px-3 h-8 ms-0 leading-tight text-gray-500 bg-white border border-gray-300 rounded-s-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white">Předchozí</a>
            </li>
            <template  v-for="page in totalPages" :key="page">
              <li v-if="Math.abs(currentPage - page) <= 3 || page === 1 || page === totalPages">
                <button @click="goToPage(page)"
                        :class="{'text-blue-600 bg-blue-50': currentPage === page, 'text-gray-500 bg-white': currentPage !== page}"
                        class="flex items-center justify-center px-3 h-8 leading-tight border border-gray-300 hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white">
                  {{ page }}
                </button>
              </li>
            </template>
            <li>
              <a href="#" @click="nextPage" :disabled="currentPage === totalPages"
                 class="flex items-center justify-center px-3 h-8 leading-tight text-gray-500 bg-white border border-gray-300 rounded-e-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white">Další</a>
            </li>
          </ul>
        </nav>
      </div>


    </default>
  </div>

</template>

<script lang="ts">
import Default from "@/layouts/admin/default.vue";
import {useAppStore} from "@/stores/appStore";
import type {ResponseEntityLog} from "@haxo-cz/orders-ts-client";

export default {
  name: "LogsView",
  components: {Default},
  data() {
    const appStore = useAppStore();
    return {
      appStore,
      logs: [] as ResponseEntityLog[],
      currentPage: 1,
      totalPages: 1, // Total number of pages, initialized
      limit: 10,
      dateFrom: "",
      dateTo: "",
      level: "",
      type: "",
      source: "",
      search: "",
      sortBy: "createdAt",
      sortOrder: "desc",
      isLoading: false, // New loading state variable
      totalCount: 0,
    };
  },
  methods: {
    async fetchLogs() {
      this.isLoading = true; // Set loading to true before fetching data
      try {
        const response = await this.$logsApi.findAllLogs(
            this.appStore.selectedProject.spaceId,
            this.appStore.selectedProject._id,
            this.currentPage,
            this.limit,
            this.dateFrom ? this.dateFrom : undefined,
            this.dateTo ? this.dateTo : undefined,
            this.level ? this.level : undefined,
            this.type ? this.type : undefined,
            this.source ? this.source : undefined,
            this.search ? this.search : undefined,
            this.sortBy,
            this.sortOrder
        );
        this.logs = response.data.data;
        this.totalPages = response.data.meta.pagination.pages;
        this.totalCount = response.data.meta.totalCount;
      } catch (error) {
        console.error("Chyba při načítání logů:", error);
      } finally {
        this.isLoading = false; // Set loading to false after data is fetched
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.isLoading = true;
        this.currentPage++;
        this.fetchLogs();
      }
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.fetchLogs();
      }
    },
    goToPage(page: number) {
      if (page !== this.currentPage) {
        this.currentPage = page;
        this.fetchLogs();
      }
    },
  },
  mounted() {
    this.fetchLogs();
  },
};
</script>

<style scoped>
/* Stylování dle potřeby */
.loader {
  border-top-color: #3498db;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

select#limit {
  height: 2rem; /* Adjust height to match buttons */
  padding-left: 0.75rem; /* Adjust padding to match buttons */
  padding-right: 2rem; /* Adjust padding to match buttons */
}
</style>
